/**
 * Patrol Detail Page - View single patrol with checkpoint scans
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { makeStyles, Text, Button, Badge, Spinner, tokens } from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  PersonWalkingRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  PersonRegular,
  BuildingRegular,
  CalendarRegular,
  CheckboxCheckedRegular,
  ScanRegular,
  MapRegular,
  ClockRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate } from '../../../components/common';
import { ApiClient, newToast, fullDateTime } from '../../../configurations/index.config';
import type { PatrolDetail, PatrolCheckpointScan } from '../../../types';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  headerIcon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '24px',
    marginBottom: '24px',
  },
  summaryCard: {
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
  },
  summaryCardTitle: {
    fontSize: '13px',
    color: '#605e5c',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  summaryCardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  summaryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: '13px',
    color: '#605e5c',
  },
  summaryValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#323130',
  },
  completionSection: {
    marginBottom: '24px',
  },
  completionCard: {
    padding: '24px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
  },
  completionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  completionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
  },
  completionStats: {
    display: 'flex',
    gap: '24px',
  },
  completionStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  completionStatValue: {
    fontSize: '24px',
    fontWeight: '600',
  },
  completionStatLabel: {
    fontSize: '12px',
    color: '#605e5c',
  },
  progressBarLarge: {
    width: '100%',
    height: '12px',
    backgroundColor: '#e1dfdd',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: '6px',
    transition: 'width 0.3s ease',
  },
  scansSection: {
    marginTop: '24px',
  },
  scansHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  scansTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  thCenter: {
    padding: '12px 16px',
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  tdCenter: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
    textAlign: 'center',
  },
  sequenceNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
  },
  mapLink: {
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '100px 20px',
  },
});

export const PatrolDetailFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [patrol, setPatrol] = useState<PatrolDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchPatrol(id);
    }
  }, [id]);

  const fetchPatrol = async (patrolId: string) => {
    setLoading(true);
    try {
      const response = await ApiClient.get(`/get-patrol/${patrolId}`);
      if (response.data.status === 'success') {
        setPatrol(response.data.data);
      } else {
        newToast('error', response.data.message || 'Failed to fetch patrol');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge appearance="filled" color="success">
            Completed
          </Badge>
        );
      case 'incomplete':
        return (
          <Badge appearance="filled" color="warning">
            Incomplete
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge appearance="outline" color="danger">
            Cancelled
          </Badge>
        );
      default:
        return (
          <Badge appearance="outline" color="informative">
            {status}
          </Badge>
        );
    }
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 90) {
      return '#107c10';
    }
    if (percentage >= 70) {
      return '#ffaa44';
    }
    return '#d13438';
  };

  const getScheduleStatusBadge = (scheduleStatus?: string) => {
    switch (scheduleStatus) {
      case 'on_schedule':
        return (
          <Badge appearance="filled" color="success">
            🟢 On Schedule
          </Badge>
        );
      case 'off_schedule':
        return (
          <Badge appearance="filled" color="warning">
            🟠 Off Schedule
          </Badge>
        );
      case 'no_window':
        return (
          <Badge appearance="outline" color="informative">
            ⚪ No Window
          </Badge>
        );
      default:
        return (
          <Badge appearance="outline" color="informative">
            —
          </Badge>
        );
    }
  };

  const formatDuration = (minutes: number) => {
    if (!minutes) {
      return '—';
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins} min`;
  };

  const formatTime = (dateString: string) => {
    if (!dateString) {
      return '—';
    }
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (loading) {
    return (
      <PermissionGate privilege="get-patrols">
        <div className={styles.container}>
          <div className={styles.loadingState}>
            <Spinner size="large" label="Loading patrol details..." />
          </div>
        </div>
      </PermissionGate>
    );
  }

  if (!patrol) {
    return (
      <PermissionGate privilege="get-patrols">
        <div className={styles.container}>
          <div className={styles.emptyState}>
            <PersonWalkingRegular
              style={{ fontSize: '48px', color: '#c8c6c4', marginBottom: '16px' }}
            />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              Patrol Not Found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              The patrol you're looking for doesn't exist or has been deleted.
            </Text>
            <Button
              appearance="primary"
              onClick={() => navigate('/account/security-operations/patrols')}
              style={{ marginTop: '16px' }}
            >
              Back to Patrols
            </Button>
          </div>
        </div>
      </PermissionGate>
    );
  }

  return (
    <PermissionGate privilege="get-patrols">
      <div className={styles.container}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={() => navigate('/account/security-operations/patrols')}
          />
          <PersonWalkingRegular className={styles.headerIcon} />
          <div className={styles.headerText}>
            <Text className={styles.pageTitle}>Patrol #{patrol.id}</Text>
            <Text className={styles.pageSubtitle}>
              {patrol.employee_name} • {patrol.site_name}
            </Text>
          </div>
          {getStatusBadge(patrol.status)}
        </div>

        {/* Summary Cards */}
        <div className={styles.summaryGrid}>
          {/* Guard Info */}
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardTitle}>
              <PersonRegular />
              Guard Information
            </div>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Name</span>
                <span className={styles.summaryValue}>{patrol.employee_name}</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Face Verified</span>
                <span className={styles.summaryValue}>
                  {patrol.face_verified ? (
                    <Badge appearance="filled" color="success" size="small">
                      <CheckmarkCircleRegular style={{ marginRight: '4px' }} />
                      Yes ({patrol.face_confidence_score?.toFixed(1)}%)
                    </Badge>
                  ) : (
                    <Badge appearance="outline" color="danger" size="small">
                      <DismissCircleRegular style={{ marginRight: '4px' }} />
                      No
                    </Badge>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Location Info */}
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardTitle}>
              <BuildingRegular />
              Location
            </div>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Site</span>
                <span className={styles.summaryValue}>{patrol.site_name}</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Client</span>
                <span className={styles.summaryValue}>{patrol.client_name}</span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Shift</span>
                <span className={styles.summaryValue}>{patrol.shift_name || '—'}</span>
              </div>
            </div>
          </div>

          {/* Time Info */}
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardTitle}>
              <CalendarRegular />
              Time & Duration
            </div>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Started</span>
                <span className={styles.summaryValue}>
                  {patrol.started_at ? fullDateTime(patrol.started_at) : '—'}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Ended</span>
                <span className={styles.summaryValue}>
                  {patrol.ended_at ? fullDateTime(patrol.ended_at) : '—'}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Duration</span>
                <span className={styles.summaryValue}>
                  {formatDuration(patrol.duration_minutes)}
                </span>
              </div>
            </div>
          </div>

          {/* Schedule Info */}
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardTitle}>
              <ClockRegular />
              Schedule Status
            </div>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Status</span>
                <span className={styles.summaryValue}>
                  {getScheduleStatusBadge(patrol.schedule_status)}
                </span>
              </div>
              {patrol.matched_window_name && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Window</span>
                  <span className={styles.summaryValue}>{patrol.matched_window_name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Completion Info */}
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardTitle}>
              <CheckboxCheckedRegular />
              Completion
            </div>
            <div className={styles.summaryCardContent}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Checkpoints</span>
                <span className={styles.summaryValue}>
                  {patrol.scanned_checkpoints} / {patrol.expected_checkpoints}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Percentage</span>
                <span
                  className={styles.summaryValue}
                  style={{ color: getCompletionColor(patrol.completion_percentage || 0) }}
                >
                  {patrol.completion_percentage?.toFixed(1) || 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Completion Progress Bar */}
        <div className={styles.completionSection}>
          <div className={styles.completionCard}>
            <div className={styles.completionHeader}>
              <span className={styles.completionTitle}>Patrol Completion Progress</span>
              <div className={styles.completionStats}>
                <div className={styles.completionStat}>
                  <span
                    className={styles.completionStatValue}
                    style={{ color: getCompletionColor(patrol.completion_percentage || 0) }}
                  >
                    {patrol.completion_percentage?.toFixed(0) || 0}%
                  </span>
                  <span className={styles.completionStatLabel}>Complete</span>
                </div>
                <div className={styles.completionStat}>
                  <span className={styles.completionStatValue} style={{ color: '#107c10' }}>
                    {patrol.scanned_checkpoints}
                  </span>
                  <span className={styles.completionStatLabel}>Scanned</span>
                </div>
                <div className={styles.completionStat}>
                  <span className={styles.completionStatValue} style={{ color: '#605e5c' }}>
                    {patrol.expected_checkpoints}
                  </span>
                  <span className={styles.completionStatLabel}>Expected</span>
                </div>
              </div>
            </div>
            <div className={styles.progressBarLarge}>
              <div
                className={styles.progressBarFill}
                style={{
                  width: `${Math.min(patrol.completion_percentage || 0, 100)}%`,
                  backgroundColor: getCompletionColor(patrol.completion_percentage || 0),
                }}
              />
            </div>
          </div>
        </div>

        {/* Checkpoint Scans Table */}
        <ContentCard>
          <div className={styles.scansHeader}>
            <span className={styles.scansTitle}>
              <ScanRegular />
              Checkpoint Scans ({patrol.checkpoint_scans?.length || 0})
            </span>
          </div>

          {!patrol.checkpoint_scans || patrol.checkpoint_scans.length === 0 ? (
            <div className={styles.emptyState}>
              <ScanRegular style={{ fontSize: '48px', color: '#c8c6c4', marginBottom: '16px' }} />
              <Text size={500} weight="semibold" style={{ display: 'block' }}>
                No Checkpoint Scans
              </Text>
              <Text style={{ marginTop: '8px', display: 'block' }}>
                No checkpoints were scanned during this patrol.
              </Text>
            </div>
          ) : (
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th className={styles.thCenter}>#</th>
                  <th className={styles.th}>Checkpoint</th>
                  <th className={styles.th}>Scanned At</th>
                  <th className={styles.thCenter}>Location</th>
                  <th className={styles.thCenter}>Distance</th>
                  <th className={styles.thCenter}>Within Boundary</th>
                </tr>
              </thead>
              <tbody>
                {patrol.checkpoint_scans.map((scan: PatrolCheckpointScan) => (
                  <tr key={scan.id} className={styles.tr}>
                    <td className={styles.tdCenter}>
                      <div className={styles.sequenceNumber}>{scan.sequence_number}</div>
                    </td>
                    <td className={styles.td}>
                      <Text weight="semibold">{scan.checkpoint_name}</Text>
                    </td>
                    <td className={styles.td}>{formatTime(scan.scanned_at)}</td>
                    <td className={styles.tdCenter}>
                      {scan.latitude && scan.longitude ? (
                        <a
                          href={`https://maps.google.com/?q=${scan.latitude},${scan.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.mapLink}
                        >
                          <MapRegular />
                          View Map
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={styles.tdCenter}>
                      {scan.distance_from_site_m !== null && scan.distance_from_site_m !== undefined
                        ? `${scan.distance_from_site_m.toFixed(1)}m`
                        : '—'}
                    </td>
                    <td className={styles.tdCenter}>
                      {scan.within_site_boundary ? (
                        <Badge appearance="filled" color="success" size="small">
                          <CheckmarkCircleRegular style={{ marginRight: '4px' }} />
                          Yes
                        </Badge>
                      ) : (
                        <Badge appearance="filled" color="danger" size="small">
                          <DismissCircleRegular style={{ marginRight: '4px' }} />
                          No
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default PatrolDetailFluentUI;
